import * as THREE from 'three';
import { Player } from './Player';
import { InputManager } from './InputManager';
import { MaskManager, MaskType } from '../masks/MaskManager';
import { LevelManager } from '../levels/LevelManager';

export class Game {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private player: Player;
  private inputManager: InputManager;
  private maskManager: MaskManager;
  private levelManager: LevelManager;
  
  private clock: THREE.Clock;
  private isRunning: boolean = false;
  private timeScale: number = 1;
  
  // UI elements
  private hintText: HTMLElement | null;
  private timeSlowOverlay: HTMLElement | null;
  private waterOverlay: HTMLElement | null;
  private shadowOverlay: HTMLElement | null;
  private winScreen: HTMLElement | null;
  
  constructor() {
    // Create renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    const container = document.getElementById('game-container');
    container?.insertBefore(this.renderer.domElement, container.firstChild);
    
    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a2e);
    this.scene.fog = new THREE.Fog(0x1a1a2e, 10, 50);
    
    // Create input manager
    this.inputManager = new InputManager();
    
    // Create player
    this.player = new Player(this.inputManager);
    
    // Create mask manager
    this.maskManager = new MaskManager(this.player, this);
    
    // Create level manager
    this.levelManager = new LevelManager(this.scene, this.player, this.maskManager);
    this.levelManager.setGame(this);
    
    // Clock for delta time
    this.clock = new THREE.Clock();
    
    // Get UI elements
    this.hintText = document.getElementById('hint-text');
    this.timeSlowOverlay = document.getElementById('time-slow-overlay');
    this.waterOverlay = document.getElementById('water-overlay');
    this.shadowOverlay = document.getElementById('shadow-overlay');
    this.winScreen = document.getElementById('win-screen');
    
    // Setup input callbacks
    this.setupInputCallbacks();
    
    // Handle window resize
    window.addEventListener('resize', () => this.onWindowResize());
    
    // Setup player collision
    this.player.checkCollision = (position, radius) => 
      this.levelManager.checkCollision(position, radius);
    this.player.checkWater = (position) => 
      this.levelManager.checkWater(position);
  }
  
  private setupInputCallbacks(): void {
    this.inputManager.onMaskSwitch = (index) => {
      this.maskManager.switchToMask(index);
    };
    
    this.inputManager.onInteract = () => {
      this.levelManager.interact(this.player.position, this.player.camera);
    };
    
    this.inputManager.onAbilityUse = () => {
      this.maskManager.useAbility();
    };
  }
  
  private onWindowResize(): void {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }
  
  public start(): void {
    this.isRunning = true;
    this.inputManager.requestPointerLock();
    
    // Load first level
    this.levelManager.loadLevel(0);
    
    // Show level name
    this.showHint(this.levelManager.getCurrentLevelName(), 4000);
    
    // Start game loop
    this.clock.start();
    this.gameLoop();
  }
  
  private gameLoop(): void {
    if (!this.isRunning) return;
    
    requestAnimationFrame(() => this.gameLoop());
    
    const deltaTime = Math.min(this.clock.getDelta(), 0.1); // Cap delta time
    
    // Update time scale based on Hermes mask
    const effectiveTimeScale = this.maskManager.isTimeSlowed() ? 0.3 : 1;
    
    // Update player
    this.player.update(deltaTime, effectiveTimeScale);
    
    // Update masks
    this.maskManager.update(deltaTime);
    
    // Update level (puzzles, etc.)
    this.levelManager.update(deltaTime * effectiveTimeScale);
    
    // Update overlays
    this.updateOverlays();
    
    // Check win condition
    if (this.levelManager.isGameComplete()) {
      this.showWinScreen();
    }
    
    // Render
    this.renderer.render(this.scene, this.player.camera);
  }
  
  private updateOverlays(): void {
    // Time slow overlay
    if (this.timeSlowOverlay) {
      this.timeSlowOverlay.classList.toggle('active', this.maskManager.isTimeSlowed());
    }
    
    // Water overlay - intensify based on drowning
    if (this.waterOverlay) {
      const drowning = this.levelManager.getDrowningProgress();
      if (this.player.isInWater && !this.player.canBreatheUnderwater) {
        this.waterOverlay.style.background = `rgba(200, 0, 0, ${0.2 + drowning * 0.5})`;
        this.waterOverlay.classList.add('active');
      } else if (this.player.isInWater) {
        this.waterOverlay.style.background = 'rgba(0, 100, 200, 0.2)';
        this.waterOverlay.classList.add('active');
      } else {
        this.waterOverlay.classList.remove('active');
      }
    }
    
    // Shadow overlay
    if (this.shadowOverlay) {
      this.shadowOverlay.classList.toggle('active', this.player.isInvisible);
    }
  }
  
  public showHint(text: string, duration: number = 3000): void {
    if (this.hintText) {
      this.hintText.textContent = text;
      this.hintText.classList.add('visible');
      
      setTimeout(() => {
        this.hintText?.classList.remove('visible');
      }, duration);
    }
  }
  
  private showWinScreen(): void {
    this.isRunning = false;
    document.exitPointerLock();
    
    if (this.winScreen) {
      this.winScreen.style.display = 'flex';
    }
  }
  
  public getScene(): THREE.Scene {
    return this.scene;
  }
  
  public getPlayer(): Player {
    return this.player;
  }
  
  public setTimeScale(scale: number): void {
    this.timeScale = scale;
  }
}
